import { useEffect, useRef } from 'react'
import type { Subscription } from '@/services/contract/contract.types.ts'
import type { Profile } from '@/hooks/useProfile.ts'
import {
  sendSubscriptionExpiringEmailWebplatform,
  sendSubscriptionExpiredEmailWebplatform,
} from '@/services/notification/webplatformEmailSenders.ts'

const THIRTY_DAYS = 86400n * 30n

function storageKey(
  kind: 'expiring' | 'expired',
  wallet: string,
  expiry: string,
) {
  return `bitslegacy_email_sub_${kind}_${wallet.toLowerCase()}_${expiry}`
}

/**
 * H / I from webplatform: expiring (~1 month left) and expired subscription emails.
 * Uses localStorage dedupe (same idea as session-scoped Sets for vault timers).
 */
export function useSubscriptionLifecycleEmails(
  subscription: Subscription | null,
  isActive: boolean | null,
  profile: Profile | null,
  walletAddress: string | undefined,
  enabled: boolean,
): void {
  const sentRef = useRef({ expiring: false, expired: false })

  useEffect(() => {
    sentRef.current = { expiring: false, expired: false }
  }, [walletAddress, subscription?.expiryTime])

  useEffect(() => {
    if (!enabled || !walletAddress || !profile?.email?.trim()) return
    if (!subscription?.exists) return

    const now = BigInt(Math.floor(Date.now() / 1000))
    const expiry = subscription.expiryTime
    const expiryKey = expiry.toString()
    const tier = Number(subscription.tier)
    const name = profile.name?.trim() || 'User'

    void (async () => {
      // H: expiring within 30 days, still active
      if (
        isActive === true &&
        expiry > now &&
        expiry - now <= THIRTY_DAYS &&
        !sentRef.current.expiring
      ) {
        const key = storageKey('expiring', walletAddress, expiryKey)
        if (!localStorage.getItem(key)) {
          const ok = await sendSubscriptionExpiringEmailWebplatform({
            to: profile.email,
            userName: name,
            tier,
            expiryTime: expiry,
            notifyEmail: profile.notify_email,
          })
          if (ok) localStorage.setItem(key, '1')
        }
        sentRef.current.expiring = true
      }

      // I: expired
      if (expiry < now && !sentRef.current.expired) {
        const key = storageKey('expired', walletAddress, expiryKey)
        if (!localStorage.getItem(key)) {
          const ok = await sendSubscriptionExpiredEmailWebplatform({
            to: profile.email,
            userName: name,
            tier,
            notifyEmail: profile.notify_email,
          })
          if (ok) localStorage.setItem(key, '1')
        }
        sentRef.current.expired = true
      }
    })()
  }, [enabled, walletAddress, profile, subscription, isActive])
}
